import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useSubscriptionFeatures } from "./useSubscriptionFeatures";
import { toast } from "./use-toast";

export interface BrandingSettings {
  enabled: boolean;
  logoUrl: string | null;
  primaryColor: string;
  accentColor: string;
  secondaryColor: string;
}

export const useOrganizationBranding = () => {
  const [branding, setBranding] = useState<BrandingSettings>({
    enabled: false,
    logoUrl: null,
    primaryColor: "#007bff",
    accentColor: "#28a745",
    secondaryColor: "#6c757d",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { organizationData } = useSubscriptionFeatures();

  const fetchBranding = async () => {
    if (!organizationData?.id) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("organization_branding")
        .select("*")
        .eq("organization_id", organizationData.id)
        .maybeSingle();

      if (error && error.code !== "PGRST116") {
        console.error("Error fetching branding:", error);
        return;
      }

      if (data) {
        setBranding({
          enabled: data.enabled ?? false,
          logoUrl: data.logo_url,
          primaryColor: data.primary_color ?? "#007bff",
          accentColor: data.accent_color ?? "#28a745",
          secondaryColor: data.secondary_color ?? "#6c757d",
        });
      }
    } catch (_error) {
      console.error("Error fetching branding:", _error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organizationData?.id) {
      fetchBranding();
    }
  }, [organizationData?.id]);

  const updateBranding = async (updates: Partial<BrandingSettings>) => {
    if (!organizationData?.id) return { error: "No organization found" };

    setSaving(true);
    try {
      const { error } = await supabase
        .from("organization_branding")
        .upsert({
          organization_id: organizationData.id,
          enabled: updates.enabled ?? branding.enabled,
          logo_url: updates.logoUrl ?? branding.logoUrl,
          primary_color: updates.primaryColor ?? branding.primaryColor,
          accent_color: updates.accentColor ?? branding.accentColor,
          secondary_color: updates.secondaryColor ?? branding.secondaryColor,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'organization_id'
        });

      if (error) {
        toast({
          title: "Erreur",
          description: "Impossible de sauvegarder les paramètres de branding",
          variant: "destructive",
        });
        return { error };
      }

      setBranding({ ...branding, ...updates });
      toast({
        title: "Branding mis à jour",
        description: "Les paramètres de branding ont été sauvegardés",
      });
      return { error: null };
    } catch (_error) {
      console.error("Error updating branding:", _error);
      return { error: _error };
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (file: File) => {
    if (!organizationData?.id) {
      toast({
        title: "Organisation requise",
        description: "Vous devez créer une organisation avant de pouvoir télécharger un logo",
        variant: "destructive",
      });
      return { error: "No organization found", url: null };
    }

    try {
      const fileName = `${organizationData.id}-logo-${Date.now()}.${file.name.split('.').pop()}`;
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("organization-logos")
        .upload(fileName, file);

      if (uploadError) {
        // Provide more specific error messages based on error type
        let errorMessage = "Impossible de télécharger le logo";
        if (uploadError.message?.includes('policy')) {
          errorMessage = "Vous devez être administrateur de l'organisation pour télécharger un logo";
        } else if (uploadError.message?.includes('file size')) {
          errorMessage = "Le fichier est trop volumineux";
        }
        
        toast({
          title: "Erreur d'upload",
          description: errorMessage,
          variant: "destructive",
        });
        return { error: uploadError, url: null };
      }

      const { data: { publicUrl } } = supabase.storage
        .from("organization-logos")
        .getPublicUrl(uploadData.path);

      await updateBranding({ logoUrl: publicUrl });
      return { error: null, url: publicUrl };
    } catch (_error) {
      console.error("Error uploading logo:", _error);
      toast({
        title: "Erreur d'upload",
        description: "Une erreur inattendue s'est produite lors de l'upload",
        variant: "destructive",
      });
      return { error, url: null };
    }
  };

  return {
    branding,
    loading,
    saving,
    updateBranding,
    uploadLogo,
    refreshBranding: fetchBranding,
  };
};